import { AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useMemo } from "react";
import FilePreview from "@/components/file/Preview";
import { EmbeddedWorkflowPreview } from "./EmbeddedWorkflowPreview";
import { EmbeddedWorkflowPreviewMobile } from "./EmbeddedWorkflowPreviewMobile";
import { useMedia } from "@/contexts/MediaContext";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { BoundingBoxStrategyImpl } from "@utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@/useTestWithMoreFiles";
import { useAddExampleData } from "@/hooks/useAddExampleData";
import { IntegrationCodeSnippet } from "@/components/flow/shared/Deploy/IntegrationCodeSnippet";
import { useDeployContext } from "@/contexts/DeployContext";
import { useAuth } from "@/contexts/AuthContext";
import { generateIntegrationCMS } from "@/components/flow/shared/Deploy/documentation/generateIntegrationCMS";
import {
    ImageDeployment,
    VideoDeployment,
    DataMode
} from "@/components/flow/shared/Deploy/documentation/types";
import { useMobile } from "@/hooks/useMobile";
import { useCurrentMedia } from "@/store";
import { VideoTrimmer } from "@/components/file/VideoTrimmer";
import { UploadLimitErrorModal } from "@/components/flow/shared/UploadLimitErrorModal";
import { OutOfCreditsModal } from "@/components/flow/shared/OutOfCreditsModal";

// Share page always shows the test interface
export function ShareVisualAreaContent() {
    const { showLabels } = useCoreApp();
    const {
        testWithMoreFiles,
        uploadLimitError,
        clearUploadLimitError,
        isOutOfCredits,
        clearOutOfCreditsError
    } = useTestWithMoreFiles();
    const { mediaItemToTrim } = useMedia();
    const currentMedia = useCurrentMedia();
    const { workflowCredentials, dataMode, workflow } = useDeployContext();
    const { user } = useAuth();

    // Responsive breakpoint detection - using md breakpoint (768px)
    const isMobile = useMobile(768);

    // Generate copy for IMAGE and VIDEO data modes
    const copy = useMemo(() => {
        // Don't generate copy for PHONE or TEST_FILES mode
        if (dataMode === DataMode.PHONE || dataMode === DataMode.TEST_FILES) {
            return null;
        }

        const integrationCMS = generateIntegrationCMS(workflowCredentials);

        // Get the appropriate deployment type based on data mode
        const getDefaultDeploymentType = () => {
            if (dataMode === DataMode.IMAGE) {
                return ImageDeployment.SINGLE;
            } else if (dataMode === DataMode.VIDEO) {
                return VideoDeployment.FILE;
            }
            return ImageDeployment.SINGLE;
        };

        const selectedDeploymentType = getDefaultDeploymentType();
        return integrationCMS[dataMode][
            selectedDeploymentType as keyof (typeof integrationCMS)[typeof dataMode]
        ];
    }, [workflowCredentials, dataMode]);

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await testWithMoreFiles(files);
            onFinally?.();
        },
        [testWithMoreFiles]
    );

    const { addExampleDataFiles } = useAddExampleData({ stable: true });

    useEffect(() => {
        addExampleDataFiles();
    }, [addExampleDataFiles]);

    return (
        <AnimatePresence mode="wait">
            <div className="visualArea relative h-full">
                <div className="flex h-full min-h-0 flex-col gap-6 overflow-hidden">
                    <FilePreviewProvider>
                        {isMobile ? (
                            // Mobile layout - single component
                            <EmbeddedWorkflowPreviewMobile onAddMediaItems={onAddMediaItems}>
                                <FilePreview
                                    key={`file-${currentMedia?.file.id}`}
                                    forceHideLabels={!showLabels}
                                    strategy={BoundingBoxStrategyImpl}
                                />
                            </EmbeddedWorkflowPreviewMobile>
                        ) : (
                            // Desktop layout - component + integration code snippet
                            <>
                                <EmbeddedWorkflowPreview
                                    className="h-[80vh] w-full"
                                    onAddMediaItems={onAddMediaItems}
                                >
                                    <FilePreview
                                        key={`file-${currentMedia?.file.id}`}
                                        forceHideLabels={!showLabels}
                                        strategy={BoundingBoxStrategyImpl}
                                    />
                                </EmbeddedWorkflowPreview>
                                {copy && (
                                    <IntegrationCodeSnippet
                                        copy={copy}
                                        blurred={!user}
                                        isExpanded={false}
                                    />
                                )}
                            </>
                        )}
                    </FilePreviewProvider>
                </div>

                {/* Video Trimmer Modal */}
                {mediaItemToTrim?.validation?.needsTrimming === true && <VideoTrimmer />}

                {/* Upload Limit Error Modal */}
                <UploadLimitErrorModal
                    isOpen={uploadLimitError !== null}
                    onClose={clearUploadLimitError}
                    errorType={uploadLimitError}
                />

                {/* Out of Credits Modal */}
                <OutOfCreditsModal isOpen={isOutOfCredits} onClose={clearOutOfCreditsError} />
            </div>
        </AnimatePresence>
    );
}
